import React from "react";
import {IPagedList} from "@reapptor-apps/reapptor-toolkit";
import {ActionType, PageCacheProvider, TextAlign} from "@reapptor-apps/reapptor-react-common";
import AuthorizedPage from "../../models/base/AuthorizedPage";
import CruiseOrder from "@/models/server/CruiseOrder";
import GetCruiseOrdersRequest from "@/models/server/requests/GetCruiseOrdersRequest";
import {
    CellAction,
    CellModel,
    ColumnActionDefinition,
    ColumnDefinition,
    ColumnType,
    DropdownAlign,
    DropdownRequiredType,
    Grid,
    PageContainer,
    PageHeader,
    PageRow
} from "@reapptor-apps/reapptor-react-components";
import User from "@/models/server/User";
import {CruiseTimeOfDay} from "@/models/Enums";
import EnumProvider from "../../providers/EnumProvider";
import Localizer from "../../localization/Localizer";

import styles from "./CruiseOrders.module.scss";

interface ICruiseOrdersProps {
}

interface ICruiseOrdersState {
}

export default class CruiseOrders extends AuthorizedPage<ICruiseOrdersProps, ICruiseOrdersState> {

    state: ICruiseOrdersState = {};

    private readonly _ordersGridRef: React.RefObject<Grid<CruiseOrder>> = React.createRef();

    private readonly _columns: ColumnDefinition[] = [
        {
            header: Localizer.cruiseOrdersColumnsAreaLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.area),
            minWidth: 100,
            settings: {
                infoAccessor: nameof<CruiseOrder>(o => o.country),
            }
        } as ColumnDefinition,
        {
            header: Localizer.cruiseOrdersColumnsCompanyLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.company),
            minWidth: 150,
        } as ColumnDefinition,
        {
            header: Localizer.cruiseOrdersColumnsNameLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.name),
            minWidth: 100,
            maxWidth: 150,
        } as ColumnDefinition,
        {
            header: Localizer.cruiseOrdersColumnsEmailAndPhoneLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.email),
            minWidth: 200,
            settings: {
                infoAccessor: nameof<CruiseOrder>(o => o.phone),
            }
        } as ColumnDefinition,
        {
            header: Localizer.cruiseOrdersColumnsOrderedAtLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.createdAt),
            format: "D",
            minWidth: 110,
        } as ColumnDefinition,
        {
            header: Localizer.cruiseOrdersColumnsInfoLanguageItemName,
            minWidth: 140,
            accessor: nameof<CruiseOrder>(o => o.groupSize),
            format: "CruiseGroupSize",
            init: (cell) => this.initCruiseGroupSizeColumn(cell),
            settings: {
                infoAccessor: nameof<CruiseOrder>(o => o.groupSizeValue),
                infoFormat: "0",
                descriptionAccessor: nameof<CruiseOrder>(o => o.comment),
            }
        } as ColumnDefinition,
        {
            group: Localizer.cruiseOrdersColumnsRouteLanguageItemName,
            header: Localizer.genericDate,
            accessor: nameof<CruiseOrder>(o => o.date),
            format: "D",
            minWidth: 110,
            settings: {
                infoAccessor: nameof<CruiseOrder>(o => o.timeOfDay),
                infoFormat: nameof(CruiseTimeOfDay),
            }
        } as ColumnDefinition,
        {
            group: Localizer.cruiseOrdersColumnsRouteLanguageItemName,
            header: Localizer.genericDepartureLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.departFrom),
            minWidth: 145,
            maxWidth: 145,
            settings: {
                infoAccessor: nameof<CruiseOrder>(o => o.departBy),
                infoFormat: "t"
            }
        } as ColumnDefinition,
        {
            group: Localizer.cruiseOrdersColumnsRouteLanguageItemName,
            header: Localizer.genericVisitingPointLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.visitPoint),
            minWidth: 145,
            maxWidth: 145,
            settings: {
                infoAccessor: (model: CruiseOrder) => this.getVisitingPointDurationInfo(model),
            }
        } as ColumnDefinition,
        {
            group: Localizer.cruiseOrdersColumnsRouteLanguageItemName,
            header: Localizer.genericArrivalLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.arriveTo),
            minWidth: 145,
            maxWidth: 145,
            settings: {
                infoAccessor: nameof<CruiseOrder>(o => o.arriveAt),
                infoFormat: "t"
            }
        } as ColumnDefinition,
        {
            group: Localizer.cruiseOrdersColumnsPassengersInfoGroupLanguageItemName,
            header: Localizer.genericQuantityLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.groupSizeValue),
            maxWidth: 100,
            textAlign: TextAlign.Center,
            init: (cell: CellModel<CruiseOrder>) => this.initPassengersColumn(cell),
            settings: {
                infoAccessor: nameof<CruiseOrder>(o => o.numberOfComingChildren),

            }
        } as ColumnDefinition,
        {
            group: Localizer.cruiseOrdersColumnsPassengersInfoGroupLanguageItemName,
            header: Localizer.cruiseOrdersColumnsAccessibilityLimitationsLanguageItemName,
            accessor: (model: CruiseOrder) => CruiseOrder.getAccessibilityLimitationsIcon(model),
            maxWidth: 145,
            textAlign: TextAlign.Center,
            type: ColumnType.Icon,
            init: (cell: CellModel<CruiseOrder>) => this.initAccessibilityLimitationsColumn(cell),
            settings: {
                descriptionAccessor: nameof<CruiseOrder>(o => o.accessibilityLimitationsComment),
            }
        } as ColumnDefinition,
        {
            header: Localizer.cruiseOrdersColumnsPriceLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.estimatedPrice),
            format: "C",
            maxWidth: 100,
            textAlign: TextAlign.Left,
        } as ColumnDefinition,
        {
            header: Localizer.cruiseOrdersColumnsStateLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.state),
            format: "CruiseOrderState",
            type: ColumnType.Enum,
            minWidth: 150,
            maxWidth: 150,
            settings: {
                required: true,
                align: DropdownAlign.Left
            }
        } as ColumnDefinition,
        {
            header: Localizer.cruiseOrdersColumnsCaptainLanguageItemName,
            accessor: nameof<CruiseOrder>(o => o.captain),
            type: ColumnType.Dropdown,
            minWidth: 150,
            maxWidth: 150,
            settings: {
                nothingSelectedText: "-",
                requiredType: DropdownRequiredType.Restricted,
                align: DropdownAlign.Left,
                fetchItems: (cell) => this.getCaptainsAsync(cell),
            }
        } as ColumnDefinition,
        {
            header: Localizer.genericActions,
            minWidth: 125,
            removable: false,
            init: (cell) => this.initOrdersOperations(cell),
            actions: [
                {
                    name: "save",
                    title: Localizer.genericSave,
                    icon: "far save",
                    type: ActionType.Create,
                    callback: async (cell, action) => await this.processOrderOperationAsync(cell, action)
                } as ColumnActionDefinition,
                {
                    name: "cancel",
                    title: Localizer.genericCancel,
                    icon: "far ban",
                    type: ActionType.Delete,
                    callback: async (cell, action) => await this.processOrderOperationAsync(cell, action)
                } as ColumnActionDefinition,
                {
                    name: "delete",
                    title: Localizer.genericDelete,
                    icon: "far trash-alt",
                    type: ActionType.Delete,
                    right: true,
                    confirm: Localizer.cruiseOrdersRemoveOrderConfirmation,
                    callback: async (cell, action) => await this.processOrderOperationAsync(cell, action)
                } as ColumnActionDefinition
            ]
        } as ColumnDefinition
    ];

    private async getCaptainsAsync(cell: CellModel<CruiseOrder>): Promise<User[]> {
        return await PageCacheProvider.getAsync("getCaptains", () => cell.grid.postAsync("api/cruise/getCaptains", null));
    }

    private async getOrdersAsync(grid: Grid<CruiseOrder>, pageNumber: number, pageSize: number): Promise<IPagedList<CruiseOrder>> {
        const request = new GetCruiseOrdersRequest();
        request.pageNumber = pageNumber;
        request.pageSize = pageSize;
        return await grid.postAsync("api/cruise/getOrders", request);
    }
    
    private getVisitingPointDurationInfo(model: CruiseOrder): string {
        return (model.visitingPointDuration)
            ? EnumProvider.getVisitingPointDurationText(model.visitingPointDuration)
            : "";
    }
    
    private initPassengersColumn(cell: CellModel<CruiseOrder>): void {
        const model: CruiseOrder = cell.model;

        cell.title = Localizer.get(Localizer.genericTotalNumber, model.groupSizeValue);

        if (model.childrenComing) {
            cell.column.settings.infoTitle = Localizer.get(Localizer.genericChildrenNumber, model.numberOfComingChildren);
        }
    }
    
    private initAccessibilityLimitationsColumn(cell: CellModel<CruiseOrder>): void {
        const model: CruiseOrder = cell.model;

        cell.descriptionReadonly = true;
        cell.column.settings.descriptionIcon = "fas info";
        
        const hideDescription: boolean = (!model.accessibilityLimitations) || (!model.accessibilityLimitationsComment);
        
        if (cell.descriptionAction && hideDescription) {
            cell.descriptionAction.visible = false;
        }
    }

    private initCruiseGroupSizeColumn(cell: CellModel<CruiseOrder>): void {
        cell.descriptionReadonly = true;
    }

    private initOrdersOperations(cell: CellModel<CruiseOrder>): void {
        //const model: CruiseOrder = cell.row.model;
        const modified: boolean = cell.row.modified;
        const deleted: boolean = cell.row.deleted;

        const saveAction: CellAction<CruiseOrder> = cell.actions[0];
        const cancelAction: CellAction<CruiseOrder> = cell.actions[1];
        const deleteAction: CellAction<CruiseOrder> = cell.actions[2];

        saveAction.visible = (modified) && (!deleted);
        cancelAction.visible = (modified) && (!deleted);
        deleteAction.visible = (!deleted);
    }

    private async processOrderOperationAsync(cell: CellModel<CruiseOrder>, action: CellAction<CruiseOrder>): Promise<void> {
        const model: CruiseOrder = cell.model;
        const isNew: boolean = (!model.id);

        if (action.action.name === "save") {

            model.captainId = model.captain ? model.captain.id : null;

            cell.row.model = await cell.grid.postAsync("api/cruise/saveCruiseOrder", model);

            await cell.row.bindAsync();

        } else if (action.action.name === "cancel") {

            await cell.row.cancelAsync();

        } else if (action.action.name === "delete") {

            if (isNew) {
                await cell.grid.deleteAsync(cell.row.index);
            } else {
                await cell.grid.postAsync("api/cruise/deleteCruiseOrder", model.id);

                await cell.grid.reloadAsync();
            }
        }
    }

    public getTitle(): string {
        return Localizer.cruiseOrdersTitle;
    }

    public render(): React.ReactNode {
        return (
            <PageContainer fullWidth className={styles.cruiseOrders}>

                <PageHeader title={this.getTitle()} />

                <PageRow>
                    <div className="col">

                        <Grid id="orders" ref={this._ordersGridRef} responsive pagination
                              minWidth={400}
                              columns={this._columns}
                              fetchData={(sender, pageNumber: number, pageSize: number) => this.getOrdersAsync(sender, pageNumber, pageSize)}
                        />

                    </div>
                </PageRow>

            </PageContainer>
        );
    }
}